import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { API_SPEC } from './api-spec.data';
import { ApiEndpoint, ApiEndpointGroup } from './api-spec.model';
import { AuthPanelComponent } from './components/auth-panel.component';
import { EndpointComponent } from './components/endpoint.component';
import { TryItPanelComponent } from './components/try-it-panel.component';
import { JsonSyntaxHighlightPipe } from './pipes/json-syntax-highlight.pipe';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-api-docs',
  standalone: true,
  imports: [CommonModule, FormsModule, AuthPanelComponent, EndpointComponent, TryItPanelComponent, JsonSyntaxHighlightPipe],
  templateUrl: './api-docs.component.html',
  styleUrls: ['./api-docs.component.sass']
})
export class ApiDocsComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly PANEL_KEY = 'wst_api_docs_panel';

  apiSpec: ApiEndpointGroup[] = API_SPEC
    .map(g => ({ ...g, endpoints: g.endpoints.filter(ep => !ep.underConstruction) }))
    .filter(g => g.endpoints.length > 0);
  apiBaseUrl = environment.apiUrl;
  token = '';
  activeSection = 'getting-started';
  mobileMenuOpen = false;
  activeEndpoint: ApiEndpoint | null = null;
  tryItPanelOpen = false;

  currentYear = new Date().getFullYear();

  paginationExample = JSON.stringify({
    data: ['...'],
    page_info: {
      current_page: 0,
      total_pages: 5,
      total_elements: 47,
      elements_per_page: 10
    }
  }, null, 2);

  errorExample = JSON.stringify({
    status: 400,
    message: 'Invalid request parameters',
    error: 'BAD_REQUEST'
  }, null, 2);

  private observer?: IntersectionObserver;

  ngOnInit(): void {
    this.loadPanelState();
  }

  ngAfterViewInit(): void {
    this.setupScrollObserver();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  onTokenChange(token: string): void {
    this.token = token;
  }

  onTryAuth(): void {
    this.activeEndpoint = null;
    this.tryItPanelOpen = true;
    this.savePanelState();
  }

  onTryEndpoint(endpoint: ApiEndpoint): void {
    this.activeEndpoint = endpoint;
    this.tryItPanelOpen = true;
    this.savePanelState();
  }

  closeTryItPanel(): void {
    this.tryItPanelOpen = false;
    this.savePanelState();
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    this.mobileMenuOpen = false;
  }

  getShortPath(path: string): string {
    return path.replace('/partner/', '');
  }

  private setupScrollObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.activeSection = entry.target.id;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px' }
    );

    document.querySelectorAll('[data-section]').forEach(el => {
      this.observer!.observe(el);
    });
  }

  private savePanelState(): void {
    try {
      localStorage.setItem(ApiDocsComponent.PANEL_KEY, JSON.stringify({
        open: this.tryItPanelOpen,
        endpointId: this.activeEndpoint?.id || null
      }));
    } catch { /* ignore */ }
  }

  private loadPanelState(): void {
    try {
      const raw = localStorage.getItem(ApiDocsComponent.PANEL_KEY);
      if (!raw) return;
      const state = JSON.parse(raw);
      this.tryItPanelOpen = !!state.open;
      if (state.endpointId) {
        for (const group of this.apiSpec) {
          const found = group.endpoints.find(ep => ep.id === state.endpointId);
          if (found) { this.activeEndpoint = found; break; }
        }
      }
    } catch { /* corrupt data */ }
  }
}
